import {
  RCS_BASIC_MAX_LENGTH,
  RCS_BUTTON_LABEL_MAX,
  RCS_SMS_MAX_LENGTH,
  RCS_TEXT_MAX_LENGTH,
  RcsButton,
  RcsContent,
  RcsMessageType,
  RcsReadCondition,
  RcsReadConditionSettings,
} from 'src/app/models/Rcs';

/**
 * Regras do RCS que a tela aplica na hora, antes da resposta do backend.
 *
 * São as mesmas do protótipo (`RCS-PROTOTIPO.html`) e do `RcsMessageClassifier` /
 * `RcsFallbackSmsBuilder` do backend. Servem só de retorno imediato enquanto o
 * `POST /api/rcs/preview` não responde: quando ele responde, é a resposta dele que
 * vai para a tela, porque é a regra do backend que decide a cobrança.
 */

const PIX_CODE_EXAMPLE = '00020126580014br.gov.bcb.pix0136a1f4c2e8-5b7d-4e19-9c3a-2f6d8e0b1a4752040000530398654062899058'
  + '02BR5911LOJA AURORA6009SAO PAULO62070503***6304B1F2';

/** Contato de exemplo da prévia. As chaves são as variáveis do painel, sem as chaves. */
export const RCS_EXAMPLE_VARIABLES: Record<string, string> = {
  first_name: 'Carlos',
  email: 'carlos@exemplo.com',
  list: 'Clientes VIP',
  campaign: 'Campanha de exemplo',
  dia_hj: 'hoje',
  cupom: 'AURORA10',
  meu_link: 'https://exemplo.com.br/oferta',
  url_checkout: 'https://exemplo.com.br/checkout/88412',
  url_pix: 'https://exemplo.com.br/pix/88412',
  url_boleto: 'https://exemplo.com.br/boleto/88412',
  data_vencimento: '25/10/2026',
  linha_digitavel: '34191.79001 01043.510047 91020.150008 5 91230026000',
  pix_copia_cola: PIX_CODE_EXAMPLE,
  pix_code: PIX_CODE_EXAMPLE,
  tracking: 'BR123456789',
  tracking_url: 'https://exemplo.com.br/rastreio/BR123456789',
  courier: 'Transportadora Exemplo',
  url_membros: 'https://exemplo.com.br/membros',
  url_lista: 'https://exemplo.com.br/lista',
  url_checkout_cross: 'https://exemplo.com.br/oferta-exclusiva',
  var1: 'valor 1',
  var2: 'valor 2',
  var3: 'valor 3',
  var4: 'valor 4',
  var5: 'valor 5',
};

/**
 * Link curto que o encurtador do backend põe no lugar de cada URL. Aqui é só um
 * marcador do mesmo tamanho, para a contagem de caracteres da prévia local não
 * enganar: o link verdadeiro chega na resposta do preview.
 */
export const RCS_LOCAL_SHORT_LINK = 'sfn.li/xxxx';

const VARIABLE_PATTERN = /\{([a-z0-9_]+)\}/gi;
const URL_PATTERN = /https?:\/\/\S+/g;

export function resolveExample(text: string, variables: Record<string, string> = RCS_EXAMPLE_VARIABLES): string {
  return (text || '').replace(VARIABLE_PATTERN, (match, key: string) =>
    Object.prototype.hasOwnProperty.call(variables, key.toLowerCase()) ? variables[key.toLowerCase()] : match);
}

/** Resolve as variáveis e troca cada link pelo marcador do link curto. */
export function resolveForMessage(text: string): string {
  return resolveExample(text).replace(URL_PATTERN, RCS_LOCAL_SHORT_LINK);
}

/** Botões com rótulo. Um botão sem texto é botão que o cliente deixou de usar. */
export function usedButtons(content: RcsContent): RcsButton[] {
  return (content?.buttons || []).filter(button => (button.label || '').trim() !== '');
}

export interface RcsClassification {
  type: RcsMessageType;
  reasons: string[];
  resolvedText: string;
}

/**
 * Single se o texto resolvido passa de 160, ou se tem título, mídia ou botão;
 * senão Basic. Os motivos são escritos para o cliente ler na caixa "Formato detectado".
 */
export function classifyContent(content: RcsContent): RcsClassification {
  if (content?.cards?.length) {
    return { type: 'single', reasons: [`tem carrossel com ${content.cards.length} cards`], resolvedText: resolveForMessage(content.cards[0]?.text || '') };
  }
  const resolvedText = resolveForMessage(content?.text || '');
  const reasons: string[] = [];

  if (resolvedText.length > RCS_BASIC_MAX_LENGTH) {
    reasons.push(`o texto tem ${resolvedText.length} caracteres, e o Basic vai até ${RCS_BASIC_MAX_LENGTH}`);
  }

  if ((content?.title || '').trim()) {
    reasons.push('tem título');
  }

  if (content?.media_url) {
    reasons.push('tem imagem');
  }

  const buttonCount = usedButtons(content).length;
  if (buttonCount) {
    reasons.push(buttonCount === 1 ? 'tem 1 botão' : `tem ${buttonCount} botões`);
  }

  return { type: reasons.length ? 'single' : 'basic', reasons, resolvedText };
}

/** O que o botão vira no SMS de fallback: o link (curto) ou o telefone. */
function buttonTail(button: RcsButton): string {
  if (button.type === 'call') {
    return (button.value || '').trim();
  }

  return RCS_LOCAL_SHORT_LINK;
}

function ellipsize(text: string, room: number): string {
  return text.length > room ? text.slice(0, room - 1).replace(/\s+$/, '') + '…' : text;
}

/**
 * SMS de quem não tem RCS (seção 9.1 da spec): o texto do card mais o link do
 * botão 1, em até 160. Título e imagem ficam de fora. Ligar só entra se couber.
 */
export function buildFallbackSms(content: RcsContent, classification = classifyContent(content)): string {
  if (content?.cards?.length) {
    return buildFallbackSms(content.cards[0], classifyContent(content.cards[0]));
  }
  const text = classification.resolvedText.replace(/\s+/g, ' ').trim();

  if (classification.type === 'basic') {
    return text.slice(0, RCS_SMS_MAX_LENGTH);
  }

  const buttons = usedButtons(content);
  if (!buttons.length) {
    return ellipsize(text, RCS_SMS_MAX_LENGTH);
  }

  const tail = ' ' + buttonTail(buttons[0]);
  if (buttons[0].type === 'call' && text.length + tail.length > RCS_SMS_MAX_LENGTH) {
    return ellipsize(text, RCS_SMS_MAX_LENGTH);
  }

  return ellipsize(text, RCS_SMS_MAX_LENGTH - tail.length) + tail;
}

const BUTTON_URL_PATTERN = /^(\{[a-z0-9_]+\}|https:\/\/\S+)$/i;

/** Problemas do conteúdo que impedem salvar ou enviar, já no texto do cliente. */
export function contentProblems(content: RcsContent): string[] {
  const problems: string[] = [];

  if (content?.cards) {
    if (content.cards.length < 2 || content.cards.length > 10) {
      problems.push('O carrossel precisa ter de 2 a 10 cards.');
    }
    content.cards.forEach((card, index) => {
      contentProblems(card).forEach(problem => problems.push(`Card ${index + 1}: ${problem}`));
    });
    return problems;
  }

  if (!(content?.text || '').trim()) {
    problems.push('Escreva o texto da mensagem.');
  }

  if ((content?.text || '').length > RCS_TEXT_MAX_LENGTH) {
    problems.push(`O texto RCS vai até ${RCS_TEXT_MAX_LENGTH} caracteres.`);
  }

  const buttons = content?.buttons || [];
  buttons.forEach((button, index) => {
    const label = (button.label || '').trim();
    const value = (button.value || '').trim();
    const position = index + 1;

    if (!label) {
      return;
    }

    if (label.length > RCS_BUTTON_LABEL_MAX) {
      problems.push(`Botão ${position}: o texto vai até ${RCS_BUTTON_LABEL_MAX} caracteres.`);
    }

    if (button.type === 'url' && !BUTTON_URL_PATTERN.test(value)) {
      problems.push(`Botão ${position}: o link precisa começar com https:// ou ser uma variável como {url_checkout}.`);
    }

    if (button.type === 'call' && value.replace(/\D/g, '').length < 10) {
      problems.push(`Botão ${position}: informe um telefone com DDD.`);
    }

    if (button.type === 'pix' && !value) {
      problems.push(`Botão ${position}: informe o código Pix ou a variável {pix_code}.`);
    }
  });

  if (buttons.length > 1 && !(buttons[0].label || '').trim() && (buttons[1].label || '').trim()) {
    problems.push('Preencha o botão 1 antes do botão 2.');
  }

  return problems;
}

/** Conteúdo que vai para a API: sem botão vazio e sem espaço sobrando. */
export function normalizeContent(content: RcsContent): RcsContent {
  if (content?.cards) {
    return { title: '', media_url: null, text: '', buttons: [], cards: content.cards.map(card => normalizeContent(card)) };
  }
  return {
    title: (content?.title || '').trim(),
    media_url: content?.media_url || null,
    text: content?.text || '',
    buttons: usedButtons(content).map(button => ({
      type: button.type,
      label: button.label.trim(),
      value: (button.value || '').trim(),
    })),
  };
}

export function emptyContent(): RcsContent {
  return { title: '', media_url: null, text: '', buttons: [] };
}

// --- Cadastro do agente -------------------------------------------------------

/** Domínio de um link https, sem `www.`. `null` se não for https válido. */
export function httpsHost(url: string): string | null {
  try {
    const parsed = new URL((url || '').trim());
    return parsed.protocol === 'https:' ? parsed.hostname.replace(/^www\./, '').toLowerCase() : null;
  } catch {
    return null;
  }
}

/** Termos e privacidade no domínio do site (ou num subdomínio dele): o Google exige. */
export function isSameDomain(host: string, siteHost: string): boolean {
  return host === siteHost || host.endsWith('.' + siteHost);
}

/**
 * CNPJ sem máscara, em maiúscula. Mantém letras: o CNPJ alfanumérico (IN RFB 2.229/2024)
 * vale desde 07/2026 e o backend aceita (`Cnpj::normalize`). Antes apagava tudo que não
 * era dígito e quebrava o alfanumérico.
 */
export function normalizeCnpj(cnpj: string): string {
  return (cnpj || '').replace(/[^0-9A-Za-z]/g, '').toUpperCase();
}

const CNPJ_WEIGHTS = [6, 5, 4, 3, 2, 9, 8, 7, 6, 5, 4, 3, 2];

function cnpjCheckDigit(base: string): string {
  // 12 caracteres usam os pesos 5..2,9..2; 13 caracteres, 6..2,9..2. Letra vale o código ASCII − 48.
  const weights = CNPJ_WEIGHTS.slice(13 - base.length);
  const sum = base.split('').reduce((total, char, index) => total + (char.charCodeAt(0) - 48) * weights[index], 0);
  const rest = sum % 11;
  return String(rest < 2 ? 0 : 11 - rest);
}

/**
 * Mesma regra do backend (`App\Support\Cnpj::isValid`): 14 caracteres (12 alfanuméricos + 2
 * dígitos), sem sequência repetida e com os dois dígitos verificadores certos.
 */
export function isValidCnpj(cnpj: string): boolean {
  const value = normalizeCnpj(cnpj);
  if (!/^[0-9A-Z]{12}[0-9]{2}$/.test(value) || /^(.)\1{13}$/.test(value)) {
    return false;
  }
  const base = value.slice(0, 12);
  const first = cnpjCheckDigit(base);
  return value.slice(12) === first + cnpjCheckDigit(base + first);
}

/** CNPJ como o cliente lê (12.345.678/0001-95, ou 12.ABC.345/01DE-35); fora de 14 caracteres volta como veio. */
export function formatCnpj(cnpj: string): string {
  const d = normalizeCnpj(cnpj);
  return d.length === 14 ? `${d.slice(0, 2)}.${d.slice(2, 5)}.${d.slice(5, 8)}/${d.slice(8, 12)}-${d.slice(12)}` : (cnpj || '');
}

export interface ImageCheck {
  ok: boolean;
  width: number;
  height: number;
  kb: number;
  message: string;
  /** `data:` da imagem, para a prévia. Só quando ela passou. */
  dataUrl?: string;
}

/**
 * Confere medida e peso da imagem no navegador, antes do envio: o broker recusa
 * logo e banner fora das medidas do Agent Builder, e descobrir isso na reprovação
 * custa uma semana da fila.
 */
export function checkImageFile(file: File, spec: { width: number; height: number; maxKb: number }): Promise<ImageCheck> {
  return new Promise(resolve => {
    const kb = Math.ceil(file.size / 1024);

    if (!/^image\/(png|jpe?g)$/.test(file.type)) {
      resolve({ ok: false, width: 0, height: 0, kb, message: 'Não é uma imagem PNG ou JPG.' });
      return;
    }

    const reader = new FileReader();
    reader.onerror = () => resolve({ ok: false, width: 0, height: 0, kb, message: 'Não foi possível ler o arquivo.' });
    reader.onload = () => {
      const dataUrl = String(reader.result);
      const image = new Image();
      image.onerror = () => resolve({ ok: false, width: 0, height: 0, kb, message: 'Não é uma imagem PNG ou JPG.' });
      image.onload = () => {
        const width = image.naturalWidth;
        const height = image.naturalHeight;
        const ok = width === spec.width && height === spec.height && file.size <= spec.maxKb * 1024;
        resolve({
          ok,
          width,
          height,
          kb,
          dataUrl: ok ? dataUrl : undefined,
          message: ok
            ? `${width}×${height}, ${kb} kB`
            : `${width}×${height}, ${kb} kB. Precisa ser ${spec.width}×${spec.height} e até ${spec.maxKb} kB.`,
        });
      };
      image.src = dataUrl;
    };
    reader.readAsDataURL(file);
  });
}

/** Letra do monograma quando o agente ainda não tem logo. */
export function brandInitial(name: string): string {
  return ((name || '?').trim().charAt(0) || '?').toUpperCase();
}

/**
 * Problemas da condição "se não ler em X h, enviar SMS" (F7.5), com as mesmas mensagens do
 * `SaveSequenceRequest`. O texto é contado CRU (antes das variáveis), como o backend valida;
 * no envio ele é resolvido, encurtado e cortado em 160 como o SMS de fallback.
 */
export function readConditionProblems(condition: RcsReadCondition, settings: RcsReadConditionSettings): string[] {
  const problems: string[] = [];
  const hours = Number(condition?.hours);
  const text = (condition?.sms_text || '').trim();

  if (!Number.isInteger(hours)) {
    problems.push('O prazo da condição por leitura é um número inteiro de horas.');
  } else if (hours < settings.min_hours) {
    problems.push(`O prazo da condição por leitura é de no mínimo ${settings.min_hours} hora.`);
  } else if (hours > settings.max_hours) {
    problems.push(`O prazo da condição por leitura é de no máximo ${settings.max_hours} horas.`);
  }

  if (text === '') {
    problems.push('Escreva o SMS que sai se a mensagem não for lida.');
  } else if (text.length > settings.sms_max_length) {
    problems.push(`O SMS da condição por leitura tem no máximo ${settings.sms_max_length} caracteres.`);
  }

  return problems;
}
